import React, { Component } from 'react';
import { string, bool, number, object, func, oneOf } from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import { Icon, Avatar } from '@fiverr-private/fit';
import { s_camera } from '@fiverr-private/icons';
import { FilerrUploader } from '@fiverr-private/filerr_uploader/src';
import { uid } from '@fiverr-private/futile';
import * as logger from '../utils/logger';
import * as stats from '../utils/stats';
import { getBadgeSize } from '../helpers/index';
import ProfileName from './components/ProfileName';
import StudioBadge from './components/StudioBadge';
import UserBadge from './components/UserBadge';
import IntroVideo, { PLAY_POSITIONS } from './components/IntroVideo';
import IntroVideoAnimationWrapper from './components/IntroVideoAnimationWrapper';
import { MIN_PROFILE_SIZE_FOR_BADGE, MODES } from './constants';
import { profileImageClicked } from './events';

import './index.scss';

const getPlayButtonPosition = (mode, isPro) => {
    if (mode === MODES.VIDEO_INTENT) {
        if (isPro) {
            return PLAY_POSITIONS.CENTER;
        }

        return PLAY_POSITIONS.BOTTOM_RIGHT;
    }

    return PLAY_POSITIONS.CENTER;
};

/**
 * @class
 * @name ProfileImage
 * @param {Object} user - all the user data
 * @param {Boolean} allowEditing - whether the user can edit
 * @param {String} authToken - Fiverr authentication token
 * Returns the markup of the user profile image
 */

class ProfileImage extends Component {

    constructor(props) {
        super(props);

        this.state = {
            file: null,
            saving: false,
            newImage: '',
            manualSavingDeferred: null,
            manualSavingPromise: null
        };

        this.onInputChange = this.onInputChange.bind(this);
        this.displayImage = this.displayImage.bind(this);
        this.updateImageFailed = this.updateImageFailed.bind(this);
        this.uniqueId = uid();
    }

    /**
     * @param {React.ChangeEvent<HTMLInputElement>} e
     * @returns {void}
     */
    onInputChange(e) {
        const { manualUpload } = this.props;
        const file = e.target.files[0];

        if (!file) {
            return;
        }

        if (manualUpload) {
            this.setState({
                file,
                newImage: URL.createObjectURL(file)
            });

            return;
        }

        this.setState({
            file,
            saving: true
        }, () => {
            this.directUploadFile();
        });
    }

    displayImage(fileUrl) {

        this.setState({
            file: null,
            saving: false,
            newImage: fileUrl
        });

    }

    saveImage({ key, bucketName, etag }) {
        const saveImageTime = Date.now();
        const formDataTemp = new FormData();

        formDataTemp.append('authenticity_token', this.props.authToken);
        formDataTemp.append('s3_file_path', key);
        formDataTemp.append('bucket_name', bucketName);

        formDataTemp.append('file_data[file_path]', key);
        formDataTemp.append('file_data[bucket_name]', bucketName);
        formDataTemp.append('file_data[etag]', etag);
        formDataTemp.append('file_data[file_size]', this.state.file.size);
        formDataTemp.append('file_data[content_type]', this.state.file.type);

        formDataTemp.append('user_id', this.props.user.user_id);

        return fetch(this.props.uploadPhotoPath, {
            method: 'POST',
            body: formDataTemp,
            headers: new Headers({
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-Token': this.props.authToken
            }),
            credentials: 'same-origin'
        }).then((response) => response.json()
            .then((data) => {
                stats.time('filerr.seller_page.update_photo', saveImageTime);

                return data;
            })
        ).catch((err) => {
            this.updateImageFailed(err);
            stats.increment('filerr.seller_page.ajax.s3_upload.error');
            logger.error('seller_card_package', 'Seller Filerr photo', `[s3_direct_upload] ${err.responseText}, status:${err.status}`, err);
        });
    }

    updateImageSucceeded(url) {
        const { onUploadSuccess } = this.props;
        const { manualSavingDeferred } = this.state;

        if (manualSavingDeferred) {
            manualSavingDeferred.resolve(url);
        }

        onUploadSuccess(url);

        this.setState({
            manualSavingDeferred: null,
            saving: false
        });
    }

    updateImageFailed(error) {
        if (this.state.manualSavingDeferred) {
            this.state.manualSavingDeferred.reject(error);
        }

        this.setState({
            file: null,
            saving: false,
            newImage: '',
            manualSavingDeferred: null
        });
    }

    /**
     * Method intended to be called by the consumer of the component when
     * the manualUpload prop is true, so that the consumer can control when
     * to upload the image.
     *
     * @see - Manual upload section of README
     * @returns {Promise<string>} - Resolves to the url of the uploaded image
     */
    async uploadImage() {
        if (!this.props.allowEditing) {
            return Promise.reject('allowEditing prop must be true to upload image.');
        }

        if (!this.props.manualUpload) {
            return Promise.reject('manualUpload prop must be true to call uploadImage.');
        }

        if (this.state.saving) {
            await this.state.manualSavingPromise;

            return this.uploadImage();
        }

        if (!this.state.file) {
            return Promise.resolve(this.props.user.profile_photo);
        }

        const manualSavingPromise = new Promise((resolve, reject) => {
            this.setState({
                saving: true,
                manualSavingDeferred: { resolve, reject }
            }, () => {
                this.directUploadFile();
            });
        });
        this.setState({ manualSavingPromise });

        return manualSavingPromise;
    }

    directUploadFile() {
        const { fetchPolicyPath } = this.props;

        if (!this.filerrUploader) {
            const filerrUploaderOptions = {
                objectName: 'profile',
                attachmentName: 'photo',
                getPolicyUrl: fetchPolicyPath,
                shouldBeRenamed: true
            };

            this.filerrUploader = new FilerrUploader(filerrUploaderOptions);
        }

        const uploadOptions = {
            file: this.state.file,
            onSuccess: (uploadedData) => this
                .saveImage(uploadedData)
                .then((savedImageData) => {
                    const { url } = savedImageData;
                    this.displayImage(url);
                    this.updateImageSucceeded(url);
                }),
            onError: this.updateImageFailed
        };

        this.filerrUploader.upload(uploadOptions);
    }

    shouldShowUserBadge() {
        const {
            user,
            isStudio,
            profileImageSize,
            mode,
            CustomBadgeComponent
        } = this.props;
        const { achievement, is_vid: isVid } = user;
        const hasAchievement = !!achievement && achievement > 0;

        if (profileImageSize < MIN_PROFILE_SIZE_FOR_BADGE) {
            return false;
        }

        if (CustomBadgeComponent) {
            return true;
        }

        return (
            mode === MODES.STANDARD &&
            (
                (hasAchievement && !isStudio) ||
                isVid
            )
        );
    }

    render() {
        const {
            user,
            className,
            allowEditing,
            profileImageSize,
            profileImageClassName,
            isNewSeller,
            showUserNameLevel,
            isUserOnline,
            onUsernameClick,
            tooltipPosition,
            noLink,
            isLimitedEdition,
            isStudio,
            isImageLazy,
            introVideo,
            origin,
            mode,
            introVideoPlayButtonClassName,
            introVideoPlayButtonIconSize,
            introVideoOverlayClassName,
            CustomBadgeComponent
        } = this.props;

        const isPro = user?.is_pro;
        const sellerName = user?.username;
        const showUserBadge = this.shouldShowUserBadge(),
            badgeSize = getBadgeSize(profileImageSize),
            newImage = this.state.newImage,
            hasImage = user.has_profile_photo || newImage;

        const introVideoPlayPosition = getPlayButtonPosition(mode, isPro);

        let userImage, userImageClasses;

        if (hasImage) {
            userImageClasses = classNames('profile-pict-img', profileImageClassName);
            userImage = (<img src={newImage || user.profile_photo}
                className={userImageClasses}
                alt={sellerName}
                loading={isImageLazy && 'lazy'}/>);
        } else {
            userImage = <Avatar containerClassName={profileImageClassName} username={user?.username} size={profileImageSize}/>;
        }

        const labelClasses = classNames(
            'profile-pict',
            {
                'editable': (allowEditing && !this.state.saving),
                'image-loading': this.state.saving
            }
        );

        const imageId = `profile_image_${this.uniqueId}`;

        const labelStyles = {
            width: `${profileImageSize}px`,
            height: `${profileImageSize}px`,
            fontSize: profileImageSize > 50 ? `${profileImageSize/50}em`: '1em'
        };

        const wrapperClasses = classNames(
            'user-profile-image',
            className
        );
        const trackImageUploadClick = () => {
            profileImageClicked({ sellerName, origin });
        };

        const content = (
            <>
                <label className={labelClasses} style={labelStyles} htmlFor={imageId}>
                    {allowEditing &&
                        <>
                            <div className="camera-icon-wrapper" onClick={trackImageUploadClick}>
                                <Icon className="camera-input-icon" size={profileImageSize / 3} color="white">
                                    {s_camera}
                                </Icon>
                            </div>
                            <input accept="image/png,image/jpeg"
                                type="file"
                                id={imageId}
                                className="hidden"
                                onChange={this.onInputChange}
                                name="profile[image]"/>
                        </>
                    }

                    {userImage}

                    {showUserBadge &&
                        <UserBadge size={badgeSize}
                            achievement={user.achievement}
                            isVid={user.is_vid}
                            CustomBadgeComponent={CustomBadgeComponent}/>
                    }

                    { introVideo && (
                        <IntroVideo videoUrl={introVideo}
                            origin={origin}
                            sellerName={user.username}
                            sellerId={user.id}
                            playPosition={introVideoPlayPosition}
                            playButtonClassName={introVideoPlayButtonClassName}
                            playButtonIconSize={introVideoPlayButtonIconSize}
                            overlayClassName={introVideoOverlayClassName}/>
                    )}
                </label>

                {showUserNameLevel &&
                    <ProfileName user={user}
                        noLink={noLink}
                        onUsernameClick={onUsernameClick}
                        isUserOnline={isUserOnline}
                        isStudio={isStudio}
                        isLimitedEdition={isLimitedEdition}
                        isNewSeller={isNewSeller}
                        tooltipPosition={tooltipPosition}/>
                }

                {showUserNameLevel && isStudio &&
                    <StudioBadge/>
                }
            </>
        );

        return (
            <div className={wrapperClasses}>
                {introVideo ? <IntroVideoAnimationWrapper content={content}/> : content}
            </div>
        );
    }
}

ProfileImage.propTypes = {
    user: object.isRequired,
    className: string,
    allowEditing: bool,
    authToken: string,
    uploadPhotoPath: string,
    fetchPolicyPath: string,
    profileImageSize: number,
    profileImageClassName: string,
    isNewSeller: bool,
    showUserNameLevel: bool,
    isUserOnline: bool,
    onUploadSuccess: func,
    onUsernameClick: func,
    tooltipPosition: string,
    noLink: bool,
    isLimitedEdition: bool,
    isStudio: bool,
    isImageLazy: bool,
    introVideo: string,
    origin: string,
    mode: oneOf(Object.values(MODES)),
    introVideoPlayButtonClassName: string,
    introVideoPlayButtonIconSize: number,
    manualUpload: bool,
    introVideoOverlayClassName: string,
    CustomBadgeComponent: func
};

ProfileImage.defaultProps = {
    allowEditing: false,
    profileImageSize: 150,
    isNewSeller: false,
    showUserNameLevel: false,
    isUserOnline: false,
    onUploadSuccess: _.noop,
    onUsernameClick: _.noop,
    tooltipPosition: 'top',
    noLink: false,
    isLimitedEdition: false,
    isStudio: false,
    mode: MODES.STANDARD,
    manualUpload: false,
    CustomBadgeComponent: null
};

ProfileImage.MODES = MODES;

export default ProfileImage;
